import React from 'react';
import { InputNumber } from 'antd';
import { toCurrency } from '../../utils/utils';
import { InputNumberProps } from 'antd/lib/input-number';
import FormInput from 'components/FormInput';
import FormItem from 'antd/lib/form/FormItem';
import { isNil } from 'lodash';

export type InputCurrencyProps = {
  currcode?: string;
  fieldname?: any;
  noStyle?: boolean;
  comcode?: string;
  col?: boolean;
  md?: number;
  xl?: number;
  rules?: any;
  required?: boolean;
  hideCurrcode?: boolean;
  className?: string;
} & InputNumberProps;

const InputCurrency = (props: InputCurrencyProps) => {
  let currencySymbol = '';

  const lcurrcode =
    props?.currcode && props?.currcode!.length > 0
      ? props?.currcode
      : localStorage.getItem('currcode');

  switch (lcurrcode) {
    case 'EUR':
      currencySymbol = '€';
      break;
    case 'MAD':
      currencySymbol = 'MAD';
      break;
    case 'XOF':
      currencySymbol = 'XOF';
      break;
    case 'TND':
      currencySymbol = 'TND';
      break;
    case 'USD':
      currencySymbol = '$';
      break;
    default:
      currencySymbol = '€';
  }

  // Calculate padding based on currency symbol length
  const getPaddingRight = () => {
    if (props.hideCurrcode) return '11px';

    // Adjust padding based on symbol length
    switch (lcurrcode) {
      case 'MAD':
      case 'XOF':
      case 'TND':
        return '45px'; // Longer text symbols need more space
      case 'EUR':
      case 'USD':
        return '25px'; // Short symbols need less space
      default:
        return '25px';
    }
  };

  return (
    <FormInput
      className={props.className}
      col={props.col}
      xs={24}
      md={props.md}
      xl={props.xl}
      name={props.fieldname}
      comcode={props.comcode}
      noStyle={props.noStyle ? true : !props.fieldname}
      style={!props.col ? { width: '100%' } : {}}
      rules={[
        { required: props.required ?? false },
        () => ({
          validator(_, value) {
            if (props.required) {
              if (Number(value) === 0 && !isNil(value)) {
                return Promise.reject(`' ${props.fieldname} ' is required`);
              }
            }
            return Promise.resolve();
          },
        }),
      ]}
      shouldUpdate
    >
      <div style={{ position: 'relative', width: '100%' }}>
        <InputNumber
          controls={false}
          className={props.className}
          min={0}
          precision={2}
          formatter={(value) => {
            if (isNil(value) || value === '') return '';

            const strValue = String(value);
            const parts = strValue.split('.');
            const intPart = parts[0];
            const decPart = parts[1];

            // Add space as thousands separator
            const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

            // Return with decimal part if it exists
            if (decPart !== undefined) {
              return `${formattedInt},${decPart}`;
            }
            return formattedInt;
          }}
          parser={(value) => {
            if (!value) return '';

            // Remove spaces and currency symbol
            let cleaned = value
              .replace(/\s/g, '')
              .replace(
                new RegExp(
                  currencySymbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                  'g'
                ),
                ''
              );

            // Replace comma with dot for consistent decimal handling
            cleaned = cleaned.replace(/,/g, '.');

            // Parse to number
            const num = Number.parseFloat(cleaned);
            return isNaN(num) ? '' : num;
          }}
          decimalSeparator=","
          style={{
            width: '100%',
            paddingRight: getPaddingRight(),
          }}
          {...props}
        />
        {!props.hideCurrcode && (
          <span
            style={{
              position: 'absolute',
              right: '11px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#8c8c8c',
              pointerEvents: 'none',
              fontSize: '14px',
              fontFamily: 'inherit',
              fontWeight: 'normal',
              lineHeight: '1',
              zIndex: 1,
            }}
          >
            {currencySymbol}
          </span>
        )}

        <style>{`
  .ant-input-number {
    width: 100% !important;
  }
  .ant-input-number-input {
    padding-right: ${getPaddingRight()} !important;
  }
`}</style>
      </div>
    </FormInput>
  );
};

export default InputCurrency;
